export type GameCatalogEntry = {
  id:
    | 'questions'
    | 'never_have_i_ever'
    | 'most_likely'
    | 'would_you_rather'
    | 'guess_fact'
    | 'compatibility'
    | 'flirt';
  title: string;
  rules: string;
};

export type GameRound = {
  id: string;
  number: number;
  state: 'OPEN' | 'REVEALED';
  prompt: unknown;
  answeredByMe: boolean;
  guessFact?: {
    role: 'AUTHOR' | 'GUESSER';
    statement?: string;
    result?: {
      authorOwn: boolean;
      statement: string;
      truth: boolean;
      guess: 'TRUE' | 'FICTION';
      guessedCorrectly: boolean;
    };
  };
  answers?: { own: boolean; answer: string | null }[];
};

export type ChatGame = {
  id: string;
  gameType: GameCatalogEntry['id'];
  status: 'INVITED' | 'ACTIVE' | 'COMPLETED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED';
  version: number;
  currentRound: number;
  round: GameRound | null;
  roundHistory: GameRound[];
  viewerActions: { accept: boolean; decline: boolean; cancel: boolean; end?: boolean };
  flirtRulesVersion?: number | null;
  compatibilityResult?: {
    task: 'compatibility_result';
    compatibilityPercent: number;
    shortVerdict: string;
    strengths: string[];
    differences: string[];
    recommendation: string;
    disclaimer: string;
  } | null;
  compatibilityResultSource?: 'PROVIDER' | 'FALLBACK' | null;
};

export function gameInviteBody(gameType: GameCatalogEntry['id'], flirtConsent = false) {
  return { gameType, ...(gameType === 'flirt' ? { flirtConsent } : {}) };
}

export function gameTitle(gameType: GameCatalogEntry['id']): string {
  return {
    questions: 'Вопросы',
    never_have_i_ever: 'Я никогда не…',
    most_likely: 'Кто из нас скорее…',
    would_you_rather: 'Что бы ты выбрал(а)?',
    guess_fact: 'Угадай факт обо мне',
    compatibility: 'Совместимость',
    flirt: 'Флирт 18+',
  }[gameType];
}

export function gameRoundLimit(gameType: GameCatalogEntry['id']): number {
  return gameType === 'guess_fact' ? 3 : 5;
}

export function gamePromptText(prompt: unknown): string {
  if (!prompt || typeof prompt !== 'object') return 'Вопрос готовится…';
  const value = prompt as {
    question?: unknown;
    left?: unknown;
    right?: unknown;
    instruction?: unknown;
  };
  if (typeof value.question === 'string') return value.question;
  if (typeof value.left === 'string' && typeof value.right === 'string')
    return `${value.left} или ${value.right}?`;
  if (typeof value.instruction === 'string') return value.instruction;
  return 'Вопрос готовится…';
}

export function gameStatusText(status: ChatGame['status']): string {
  return {
    INVITED: 'Приглашение ожидает ответа',
    ACTIVE: 'Игра идёт',
    COMPLETED: 'Игра завершена',
    DECLINED: 'Приглашение отклонено',
    EXPIRED: 'Приглашение истекло',
    CANCELLED: 'Игра остановлена',
  }[status];
}

export function gameAnswerLabel(own: boolean, counterpartName?: string): string {
  return own ? 'Вы' : counterpartName?.trim() || 'Собеседник';
}

export function gameHistoryTitle(roundCount: number): string {
  if (roundCount === 1) return 'Показать всю историю — 1 раунд';
  if (roundCount >= 2 && roundCount <= 4) return `Показать всю историю — ${roundCount} раунда`;
  return `Показать всю историю — ${roundCount} раундов`;
}

export function guessFactRoleText(role: 'AUTHOR' | 'GUESSER') {
  return role === 'AUTHOR' ? 'Ваш ход: напишите утверждение' : 'Ваш ход: угадайте';
}

export function gameQuickAnswers(
  gameType: GameCatalogEntry['id'],
  prompt?: unknown,
): string[] | null {
  if (gameType === 'never_have_i_ever') return ['Было', 'Не было'];
  if (gameType === 'most_likely') return ['Скорее я', 'Скорее ты'];
  if (gameType === 'would_you_rather') {
    const choices = prompt as { left?: unknown; right?: unknown } | undefined;
    if (typeof choices?.left === 'string' && typeof choices.right === 'string')
      return [choices.left, choices.right];
    return ['Первый вариант', 'Второй вариант'];
  }
  return null;
}
